import {
  AppLanguage,
  isAppLanguage,
  resolveSupportedAppLanguage,
} from '@/shared/lib/appLanguageSupports';
import {getManualAndAppLanguage} from '@/shared/lib/localStorage/i18n/manualAndAppLanguage';
import {getSessionLanguage} from '@/shared/lib/sessionStorage';
import {isOnTheSomeRoutePath} from '@/shared/lib/router/isOnTheSomeRoutePath';
import {RoutePattern} from '@/shared/types/routes';

import {getLanguageFromUrl} from './getLanguageFromUrl';

/**
 * Routes without a `($lang)` segment. Their language cannot come from the URL,
 * so it comes from whatever the user last chose inside the app.
 */
const APP_LOCALIZED_ROUTES: readonly RoutePattern[] = ['/app/*', '/checkout/*'];

/**
 * The language a URL should render in.
 *
 * Kept pure and free of `window` so the same decision can be made at `init()`
 * and again on every client navigation — the two have to agree, otherwise
 * entering `/app` from `/` keeps the language that `/` resolved to.
 */
export function detectLanguage(
  url: string,
  preferredLanguages: readonly string[] = []
): AppLanguage {
  const sessionLanguage = getSessionLanguage();

  if (sessionLanguage) {
    return sessionLanguage;
  }

  const {pathname} = new URL(url);

  if (isOnTheSomeRoutePath(APP_LOCALIZED_ROUTES, pathname)) {
    const appLanguage = getManualAndAppLanguage();

    if (appLanguage && isAppLanguage(appLanguage)) {
      return appLanguage;
    }

    return resolveSupportedAppLanguage(preferredLanguages) ?? 'en';
  }

  const urlLang = getLanguageFromUrl(url) ?? '';

  if (isAppLanguage(urlLang)) {
    return urlLang;
  }

  return 'en';
}
